/* How the composer's preview lays a landing page out in its pane: the device it is drawn for, the size the document is
   laid out at and the scale it is shown at. Pure, so the sizing is tested without a browser. */

/** The widths the page is previewed at. */
export type PreviewDevice = 'desktop' | 'mobile';

/** The CSS width each device lays a page template out at (a desktop viewport, a phone). Layout. */
export const DEVICE_W: Record<PreviewDevice, number> = { desktop: 1280, mobile: 390 };

/** The real device viewport a full-screen game is laid out at: the phone's, and the desktop's reference screen (a
 *  desktop game takes the pane's own shape, see {@link GAME_ASPECT}). Layout. */
export const DEVICE_VIEWPORT: Record<PreviewDevice, { w: number; h: number }> = {
  desktop: { w: 1440, h: 900 },
  mobile: { w: 390, h: 844 },
};

/** The widest and the tallest screen a desktop game is laid out at (16:9 to 4:3): its layout is responsive, so within
 *  them it is what a visitor with a window of the pane's shape sees. */
export const GAME_ASPECT = { wide: 16 / 9, tall: 4 / 3 };

/** The height of the mock browser bar over the page (CSS px, never scaled). Layout. */
export const BROWSER_BAR_H = 44;

/** The frame's hairline border, on each side (CSS px). Layout. */
export const FRAME_EDGE = 1;

/** A page height in document pixels is rounded down to this many, so a pixel's resize does not remount the document
 *  (every height change does, and a game restarts). Layout. */
const DOC_H_STEP = 10;

export interface FrameLayout {
  /** The size the document is laid out at (CSS px). */
  docW: number;
  docH: number;
  /** The shrink it is shown at (never above 1). */
  scale: number;
}

/** The scale that fits a `w` x `h` document into a `maxW` x `maxH` box, never enlarging. Pure. */
const fit = (w: number, h: number, maxW: number, maxH: number): number => Math.min(1, maxW / w, maxH / h);

const stepDown = (h: number): number => Math.floor(h / DOC_H_STEP) * DOC_H_STEP;

/**
 * The document size and scale for a `w` x `h` page box (the pane less the browser bar), or null before the pane is
 * measured. Pure.
 *
 * - A page template is laid out at its device's width, scaled to the box's width (never enlarged), as tall as the box
 *   (the page scrolls inside it).
 * - A phone game is a real 390x844 phone fitted into the box.
 * - A desktop game is 1440 wide and as tall as the box's own shape, kept between 16:9 and 4:3: a taller box shows a
 *   4:3 screen (the frame then ends under it), a wider one a 16:9 screen fitted to the box's height.
 */
export const landingFrameLayout = (args: {
  device: PreviewDevice;
  immersive: boolean;
  w: number;
  h: number;
}): FrameLayout | null => {
  const { device, immersive, w, h } = args;
  if (w <= 0 || h <= 0) return null;
  if (!immersive) {
    const docW = DEVICE_W[device];
    const scale = Math.min(1, w / docW);
    return { docW, docH: Math.max(DOC_H_STEP, stepDown(h / scale)), scale };
  }
  if (device === 'mobile') {
    const { w: docW, h: docH } = DEVICE_VIEWPORT.mobile;
    return { docW, docH, scale: fit(docW, docH, w, h) };
  }
  const docW = DEVICE_VIEWPORT.desktop.w;
  const minH = Math.round(docW / GAME_ASPECT.wide);
  const maxH = Math.round(docW / GAME_ASPECT.tall);
  const byWidth = Math.min(1, w / docW);
  const docH = Math.min(maxH, Math.max(minH, stepDown(h / byWidth)));
  return { docW, docH, scale: fit(docW, docH, w, h) };
};
